import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap, Sparkles, Shield, Layers, Users, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DemoCalculator } from "@/components/DemoCalculator";
import { useAuth } from "@/contexts/AuthContext";

const BLANKS = ["Bill", "Trip", "Dinner", "Rent", "Brunch", "Weekend", "Uber", "Cake"];

const Typewriter = () => {
  const [wordIdx, setWordIdx] = useState(0);
  const [text, setText] = useState("");
  const [phase, setPhase] = useState("typing"); // typing | pausing | deleting

  useEffect(() => {
    const current = BLANKS[wordIdx];
    let timeout;
    if (phase === "typing") {
      if (text.length < current.length) {
        timeout = setTimeout(() => setText(current.slice(0, text.length + 1)), 80);
      } else {
        timeout = setTimeout(() => setPhase("pausing"), 1400);
      }
    } else if (phase === "pausing") {
      timeout = setTimeout(() => setPhase("deleting"), 300);
    } else {
      if (text.length > 0) {
        timeout = setTimeout(() => setText(text.slice(0, -1)), 40);
      } else {
        setWordIdx((i) => (i + 1) % BLANKS.length);
        setPhase("typing");
      }
    }
    return () => clearTimeout(timeout);
  }, [text, phase, wordIdx]);

  return (
    <span className="relative inline-block" data-testid="hero-typewriter">
      <span className="brand-text">{text}</span>
      <span className="inline-block w-[2px] h-[0.85em] brand-bg ml-1 align-middle animate-pulse" />
    </span>
  );
};

const FEATURES = [
  { icon: Zap, title: "Debt simplification", text: "6 people, 3 transactions. Not 15." },
  { icon: Sparkles, title: "AI spending insights", text: "Spot patterns before they cost you." },
  { icon: Receipt, title: "Receipt OCR", text: "Snap a photo — we'll do the math." },
  { icon: Layers, title: "5 split modes", text: "Equal, exact, %, shares, itemized." },
  { icon: Users, title: "Groups for anything", text: "Trips, flats, dinners, offices." },
  { icon: Shield, title: "Your data, locked", text: "End-to-end secure. Export anytime." },
];

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      {/* Ambient background */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(0,255,102,0.18), transparent 70%), radial-gradient(ellipse 40% 30% at 80% 100%, rgba(0,122,255,0.12), transparent 60%)",
        }}
      />
      <div aria-hidden className="absolute inset-0 noise-overlay" />

      {/* Nav */}
      <nav className="relative z-10 flex items-center justify-between px-6 sm:px-12 py-6">
        <Link to="/" className="flex items-center gap-2 font-heading font-bold text-xl" data-testid="landing-logo">
          <span className="h-6 w-6 rounded-md brand-bg" />
          Split the <span className="brand-text">____</span>
        </Link>
        <div className="flex items-center gap-3">
          {user ? (
            <Link to="/dashboard">
              <Button data-testid="nav-open-dashboard" className="brand-bg text-black hover:opacity-90 active:scale-95 transition">
                Open Dashboard <ArrowUpRight className="ml-1 h-4 w-4" strokeWidth={1.5} />
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login" className="hidden sm:block">
                <Button variant="ghost" data-testid="nav-login-btn" className="text-white/70 hover:text-white">Log in</Button>
              </Link>
              <Link to="/signup">
                <Button data-testid="nav-signup-btn" className="brand-bg text-black hover:opacity-90 active:scale-95 transition">
                  Start free <ArrowUpRight className="ml-1 h-4 w-4" strokeWidth={1.5} />
                </Button>
              </Link>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <section className="relative z-10 px-6 sm:px-12 pt-16 pb-32 sm:pt-24 sm:pb-48 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1.5 text-xs font-mono uppercase tracking-[0.18em] text-white/60 mb-6">
              <span className="h-1.5 w-1.5 rounded-full brand-bg animate-pulse" />
              V1 — Bill Splitting, Reimagined
            </div>

            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter font-bold">
              Split the <Typewriter />
              <br />
              <span className="text-white/80">Not the friendship.</span>
            </h1>

            <p className="mt-7 text-lg text-white/60 leading-relaxed max-w-xl">
              A bill-splitter built like a premium tool. Smart debt simplification,
              AI insights, receipt OCR, and a dashboard that actually feels good to open.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to={user ? "/dashboard" : "/signup"}>
                <Button data-testid="hero-cta-primary" size="lg" className="brand-bg text-black font-semibold px-6 h-12 hover:opacity-90 active:scale-95 transition">
                  {user ? "Open dashboard" : "Start splitting — it's free"}
                  <ArrowUpRight className="ml-1 h-4 w-4" strokeWidth={1.5} />
                </Button>
              </Link>
              <a href="#demo" className="text-sm text-white/60 hover:text-white transition flex items-center gap-1.5" data-testid="hero-demo-link">
                <span className="h-1 w-1 rounded-full bg-white/60" />
                Try the live demo →
              </a>
            </div>

            <div className="mt-14 flex items-center gap-8 text-xs font-mono uppercase tracking-[0.2em] text-white/40">
              <span>No credit card</span>
              <span className="h-3 w-px bg-white/10" />
              <span>Unlimited bills</span>
              <span className="h-3 w-px bg-white/10" />
              <span>All currencies</span>
            </div>
          </motion.div>

          {/* Demo widget */}
          <motion.div
            id="demo"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <DemoCalculator />
          </motion.div>
        </div>
      </section>

      {/* Features grid */}
      <section className="relative z-10 px-6 sm:px-12 pb-32 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-3">/ 02 Why it's different</div>
            <h2 className="font-heading text-3xl sm:text-5xl tracking-tighter max-w-xl">
              The splitter that treats you like a power user.
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, idx) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: idx * 0.05 }}
              className="group relative rounded-xl border border-white/10 bg-card/40 p-6 hover:border-white/20 transition-colors"
              data-testid={`feature-card-${idx}`}
            >
              <div className="h-10 w-10 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center mb-5 group-hover:border-[var(--brand)]/40 transition-colors">
                <f.icon className="h-5 w-5 brand-text" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl mb-2 tracking-tight">{f.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{f.text}</p>
              <div className="mt-6 text-[10px] font-mono uppercase tracking-[0.25em] text-white/30">
                0{idx + 1} / 0{FEATURES.length}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 px-6 sm:px-12 pb-32 max-w-7xl mx-auto">
        <div className="rounded-2xl border border-white/10 bg-card/40 p-10 sm:p-16 text-center tracing-beam overflow-hidden relative">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40 mb-4">/ Ready</div>
          <h2 className="font-heading text-4xl sm:text-6xl tracking-tighter mb-5">
            Stop doing <span className="brand-text">math</span> with friends.
          </h2>
          <p className="text-white/60 text-lg max-w-lg mx-auto mb-10">
            Create your first group in 10 seconds.
          </p>
          <Link to={user ? "/dashboard" : "/signup"}>
            <Button data-testid="final-cta-btn" size="lg" className="brand-bg text-black font-semibold px-8 h-12 hover:opacity-90 active:scale-95 transition">
              {user ? "Go to Dashboard" : "Create account"}
              <ArrowUpRight className="ml-1 h-4 w-4" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="relative z-10 px-6 sm:px-12 py-10 border-t border-white/5 text-xs font-mono uppercase tracking-[0.2em] text-white/30 flex items-center justify-between flex-wrap gap-4">
        <span>© 2026 Split the ____</span>
        <span>Built with obsession.</span>
      </footer>
    </div>
  );
}
